import { useCallback } from 'react';
import useAxios from '@Apis/useAxios';
import {
    GET_PRODUCT_OPTIONS_API,
    POST_PRODUCT_OVERVIEW_API,
    GET_PRODUCT_DETAIL_API,
    POST_PRODUCT_API,
    PUT_PRODUCT_API,
    PUT_PRODUCT_ENABLED_API,
    GET_SALE_CATEGORIES_API,
    GET_SEARCH_SALE_OPTIONS_API,
} from '@Apis/apiPath';
import {
    isMock,
    mockGetProductOptions,
    mockPostProductOverview,
    mockGetProductDetail,
    mockPostProduct,
    mockPutProduct,
    mockPutProductEnabled,
} from '@Local/Product/productMockApi';
import { toNumber } from '@Util/utils';

/**
 * HQ API 成敗判斷規則：
 * 1. resp?.isError 為真 → 欄位驗證失敗，resp.result 為欄位錯誤陣列
 * 2. !resp → 失敗，useAxios 已顯示 snackbar
 * 3. 其餘 → 成功，resp 即後端 result
 */

/** 商品管理 API，mock 模式下改走 mock 實作 */
const useProductApi = () => {
    const Axios = useAxios();

    /**
     * 取得商品選項（分類樹、上下架選項）
     * @param {object} [params]
     * @param {boolean} [params.skipAll] 為 true 時分類不含「全部」
     */
    const getProductOptionsApi = useCallback(async (params) => {
        if (isMock()) return mockGetProductOptions(params);
        return await Axios.get(GET_PRODUCT_OPTIONS_API, params);
    }, []);

    /**
     * 商品總覽查詢
     * @param {object} params
     * @param {string} [params.searchText] 關鍵字（商品名稱/SKU）
     * @param {string|number} [params.isEnabled] 上下架狀態（0 全部、1 下架、2 上架）
     * @param {string|number} [params.categoryID] 第一層分類（0 全部）
     * @param {number} [params.pageIndex] 頁碼，預設 1
     * @param {number} [params.pageSize] 每頁筆數，預設 20
     * @returns {Promise<{list:Array, pageIndex:number, pageSize:number, totalCount:number, totalPage:number}>}
     */
    const postProductOverviewApi = useCallback(async (params) => {
        const pageIndex = Number(params?.pageIndex) || 1;
        const pageSize = Number(params?.pageSize) || 20;

        const searchText = params?.searchText ?? '';
        const isEnabled = toNumber(params?.isEnabled, 0);
        const categoryID = toNumber(params?.categoryID, 0);

        if (isMock()) return mockPostProductOverview({ searchText, isEnabled, categoryID, pageIndex, pageSize });

        return await Axios.post(POST_PRODUCT_OVERVIEW_API, { searchText, isEnabled, categoryID, pageIndex, pageSize });
    }, []);

    /**
     * 商品明細／編輯讀取
     * @param {number} productID
     */
    const getProductDetailApi = useCallback(async (productID) => {
        if (isMock()) return mockGetProductDetail(productID);
        return await Axios.get(GET_PRODUCT_DETAIL_API(productID));
    }, []);

    /**
     * 新增商品
     * @param {object} params 由 buildProductPayload 組成
     * @returns {Promise<number>} 新商品 productID
     */
    const postProductApi = useCallback(async (params) => {
        if (isMock()) return mockPostProduct(params);
        return await Axios.post(POST_PRODUCT_API, params);
    }, []);

    /**
     * 編輯商品（不含上下架）
     * @param {number} productID
     * @param {object} params 由 buildProductPayload 組成
     * @returns {Promise<boolean>} 成功回傳 true
     */
    const putProductApi = useCallback(async (productID, params) => {
        if (isMock()) return mockPutProduct(productID, params);
        return await Axios.put(PUT_PRODUCT_API(productID), params);
    }, []);

    /**
     * 商品上下架（商品與所有規格一起）
     * @param {number} productID
     * @param {boolean} isEnabled
     * @returns {Promise<boolean>} 成功回傳 true
     */
    const putProductEnabledApi = useCallback(async (productID, isEnabled) => {
        if (isMock()) return mockPutProductEnabled(productID, isEnabled);
        return await Axios.put(PUT_PRODUCT_ENABLED_API, { productID, isEnabled });
    }, []);

    /** 取得一般商品與健康產品兩棵銷售分類樹（抵用券商品限制使用） */
    const getSaleCategoriesApi = useCallback(async () => await Axios.get(GET_SALE_CATEGORIES_API), []);

    /**
     * 搜尋抵用券可選商品（名稱或 SKU，至少 3 字），不顯示全畫面遮罩
     * @param {string} text 搜尋文字
     */
    const getSearchSaleOptionsApi = useCallback(async text => await Axios.get(GET_SEARCH_SALE_OPTIONS_API, { text }, { isLoadingVisible: false }), []);

    return {
        getProductOptionsApi,
        postProductOverviewApi,
        getProductDetailApi,
        postProductApi,
        putProductApi,
        putProductEnabledApi,
        getSaleCategoriesApi,
        getSearchSaleOptionsApi,
    };
};

export default useProductApi;
